import { useEffect, useRef, useState } from "preact/hooks";
import type { PaymentAmountSelection } from "../../features/checkout/amountSelection";
import type { PendingPayment } from "../../features/checkout/checkoutTypes";
import type { DispatchProV2PaymentSummary } from "../../features/checkout/payment";
import {
  createXplorPendingPayment,
  getXplorPaymentToken,
  getXplorScriptUrl,
  getXplorSdk,
  mountXplorForm,
  xplorPaymentFormId,
} from "../../features/checkout/providers/xplorProvider";
import type { XplorSdk } from "../../features/checkout/providers/xplorProvider";
import { useExternalScript } from "../../lib/externalAssets";
import { formatMoney } from "../../lib/format";
import { getApiFailureMessage, useCreateTemporaryCardMutation } from "../../store/gboApi";

type Props = {
  customerId: string;
  invoiceId?: string;
  initialZipCode?: string;
  payment: DispatchProV2PaymentSummary;
  paymentAmount: PaymentAmountSelection;
  onBack: () => void;
  onPaymentReady: (payment: PendingPayment) => void;
};

export function XplorPaymentPanel({
  customerId,
  invoiceId,
  initialZipCode = "",
  payment,
  paymentAmount,
  onBack,
  onPaymentReady,
}: Props) {
  const scriptStatus = useExternalScript(getXplorScriptUrl(payment.gatewayUrl));
  const [createTemporaryCard] = useCreateTemporaryCardMutation();
  const [zipCode, setZipCode] = useState(initialZipCode);
  const [submitting, setSubmitting] = useState(false);
  const [formReady, setFormReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sdk = useRef<XplorSdk | null>(null);

  useEffect(() => {
    if (scriptStatus !== "ready") return;

    setFormReady(false);
    const loadedSdk = getXplorSdk();
    if (!loadedSdk) {
      setError("Payment options could not be loaded. Refresh the page or try again later.");
      return;
    }

    try {
      const unmount = mountXplorForm(loadedSdk, payment);
      sdk.current = loadedSdk;
      setFormReady(true);
      return () => {
        sdk.current = null;
        unmount();
      };
    } catch {
      setError("Payment options could not be loaded. Refresh the page or try again later.");
    }
  }, [payment.gatewayUrl, payment.publicKey, scriptStatus]);

  async function onSubmit(event: Event) {
    event.preventDefault();
    setError(null);

    if (paymentAmount.customAmountError) {
      setError(paymentAmount.customAmountError);
      return;
    }

    if (!sdk.current || !zipCode.trim()) return;

    setSubmitting(true);
    try {
      const mobileJwt = await getXplorPaymentToken(sdk.current);
      const temporaryCard = await createTemporaryCard({
        request: {
          customerId,
          token: mobileJwt,
          zipCode: zipCode.trim(),
        },
      }).unwrap();
      if (temporaryCard.processor !== "DispatchProV2")
        throw new Error("The payment provider returned an invalid card.");

      onPaymentReady(
        createXplorPendingPayment({
          customerId,
          invoiceId,
          payment,
          paymentAmount,
          tempCard: temporaryCard,
        }),
      );
    } catch (error) {
      setError(
        getApiFailureMessage(
          error,
          "Unable to securely read the card. Verify the card details and try again.",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  }

  const providerFailed = scriptStatus === "error";

  return (
    <section class="payment-panel" aria-label="Payment">
      {providerFailed ? (
        <div class="payment-provider-shell" data-provider={payment.provider}>
          <p role="alert">
            Payment options could not be loaded. Refresh the page or try again later.
          </p>
        </div>
      ) : (
        <form class="xplor-form" data-sentry-block onSubmit={onSubmit}>
          <div class="payment-step-summary">
            <span>Payment amount</span>
            <strong>{formatMoney(paymentAmount.amount)}</strong>
            <button class="link-action" type="button" onClick={onBack} disabled={submitting}>
              Change
            </button>
          </div>

          <div class="xplor-field">
            <span>Card information</span>
            <div id={xplorPaymentFormId}></div>
          </div>

          <label>
            <span>Billing ZIP code</span>
            <input
              value={zipCode}
              onInput={(event) => setZipCode(event.currentTarget.value)}
              autocomplete="postal-code"
              inputMode="numeric"
              required
            />
          </label>

          {payment.surchargePercentage != null && payment.surchargePercentage > 0 && (
            <p class="payment-note">
              Credit card payments may include a {payment.surchargePercentage}% surcharge. Debit and
              prepaid cards are not surcharged.
            </p>
          )}

          {error && (
            <p class="payment-error" role="alert">
              {error}
            </p>
          )}

          <button
            class="primary-action"
            type="submit"
            disabled={
              !formReady ||
              submitting ||
              !zipCode.trim() ||
              Boolean(paymentAmount.customAmountError)
            }
          >
            {submitting ? "Submitting..." : `Pay ${formatMoney(paymentAmount.amount)}`}
          </button>
        </form>
      )}
    </section>
  );
}
